<template>
	<div>
		<Modal
			v-if="!showExportModal"
			:title="labels.title"
			:type="modalTypeComputed"
			:full-screen="isMobileWebApp"
			:blocking="isMobileWebApp"
			@close="close"
		>
			<template #body>
				<div class="video-create-info">
					<div v-html="labels.description" />
				</div>
				<div class="form-group">
					<FormField
						:label="labels.fields.title"
					>
						<FormInput
							v-model="title"
						/>
					</FormField>
				</div>
				<div class="form-group info">
					<div class="title">
						{{ labels.introOutro.title }}
						<div
							v-tooltip="{content: introOutroNotAvailable ? labels.introOutro.notAvailable : labels.introOutro.message, classes: 'tooltip'}"
							class="duration-info"
						>
							<Icon
								icon-name="c-info"
								size="sm"
							/>
						</div>
					</div>
				</div>
				<div class="form-group">
					<FormField
						:label="labels.fields.intro"
					>
						<VideosDropdown
							v-model="videoIntro"
							:disabled="introOutroNotAvailable"
							:videos="editorAssetVideos"
							:is-small="true"
							:searchable="!isMobileWebApp"
							:placeholder="labels.fields.introPlaceholder"
						/>
					</FormField>
				</div>
				<div class="form-group">
					<FormField
						:label="labels.fields.outro"
					>
						<VideosDropdown
							v-model="videoOutro"
							:disabled="introOutroNotAvailable"
							:videos="editorAssetVideos"
							:is-small="true"
							:searchable="!isMobileWebApp"
							:placeholder="labels.fields.outroPlaceholder"
						/>
					</FormField>
				</div>
				<div class="form-group">
					<div class="title">
						{{ labels.fields.settings }}
					</div>
				</div>
				<div class="form-group">
					<FormField :label="labels.fields.categoryParent">
						<Dropdown
							v-model="categoryParent"
							:is-small="true"
							:options="categoryParentOptions"
						/>
					</FormField>
				</div>
				<div class="form-group info">
					<FormField
						:label="labels.copyTags.title"
						:info="labels.copyTags.message"
					>
						<SwitchInput
							v-model="copyTags"
						/>
					</FormField>
				</div>
				<div
					v-if="audioFadeAvailable"
					class="form-group info"
				>
					<FormField
						:label="labels.enableAudioFade.title"
						:info="labels.enableAudioFade.message"
					>
						<SwitchInput
							v-model="enableAudioFade"
						/>
					</FormField>
				</div>
			</template>
			<template #footer-buttons>
				<div class="form-actions">
					<button
						class="button button--primary"
						data-qa="save-tag-button"
						:disabled="!canSave"
						@click="save"
					>
						{{ transUcfirst('commons.save') }}
					</button>
					<DiscardButton
						:disabled="!hasChanges"
						@click.native="initFields"
					/>
				</div>
			</template>
		</Modal>
		<Modal
			v-if="showExportModal"
			:title="trans('one.video_export.title')"
			:full-screen="true"
			:blocking="true"
			:closeable="false"
			modal-name="export-progress-modal"
		>
			<template #body>
				<div v-if="isExporting">
					<loading-spinner
						size="large"
						class="h-flex-vertical-center h-margin-auto"
					/>
					<div class="progress">
						<span v-if="isCreatingTags">
							{{ trans('one.video_export.progress.copy_tags') }}
						</span>
						<span v-else>
							{{ trans('one.video_export.progress.create_video') }}
						</span>
					</div>
				</div>
				<div v-else-if="exportSuccessfull">
					<div class="status">
						<Icon
							icon-name="check-circle"
							size="xl"
							class="mb-5 mr-2"
						/>
						<span v-html="trans('one.video_export.progress.success', { link: createdVideoLink })" />
					</div>
				</div>
				<div v-else-if="exportFailed">
					<div class="status">
						{{ trans('one.video_export.progress.failed') }}
					</div>
				</div>
			</template>
			<template #footer-buttons>
				<div class="form-actions ">
					<button
						v-if="isExporting"
						class="button button--secondary h-width-full"
						@click="cancel()"
					>
						{{ transUcfirst('commons.cancel') }}
					</button>
					<button
						v-if="!isExporting"
						class="button button--primary h-width-full"
						:disabled="isExporting"
						@click="close()"
					>
						{{ transUcfirst('commons.close') }}
					</button>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import FormInput from '@videotools/js/one/components/Form/FormInput';
import { mapActions, mapGetters } from 'vuex';
import FormField from '@videotools/js/one/components/Form/FormField';
import Dropdown from '@components/dropdown';
import SwitchInput from '@components/Switch/Switch';
import DiscardButton from '@components/buttons/DiscardButton';
import VideosDropdown from '@videotools/js/one/components/Video/VideosDropdown';
import Modal from '@components/modal/Modal';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import laroute from '@laroute';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';

export default {
	name:       'VideoCreateForm',
	components: { Modal, VideosDropdown, DiscardButton, FormField, FormInput, Dropdown, SwitchInput, LoadingSpinner },
	props:      {
		eventTags: {
			type:    Array,
			default: () => [],
		},
		eventTagGraphics: {
			type:    Array,
			default: () => [], // [{eventTagId: 1, graphicId: 2}]
		},
		modalType: {
			type:    String,
			default: null,
		},
	},
	data() {
		return {
			title:             '',
			videoIntro:        null,
			videoOutro:        null,
			canSave:           true,
			categoryParent:    null,
			copyTags:          false,
			enableAudioFade:   true,
			isExporting:       false,
			isCreatingTags:    false,
			exportSuccessfull: false,
			exportFailed:      false,
			showExportModal:   false,
			createdVideoLink:  null,
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			event:             'event/event',
			isLive:            'event/isLive',
			editorAssetVideos: 'videoCreate/editorAssetVideos',
		}),
		modalTypeComputed() {
			if (this.isMobileWebApp) {
				return 'fullscreen';
			}

			return this.modalType || 'fit-parent';
		},
		labels() {
			return {
				title:       this.transUcfirst('commons.type_create', { type: this.transChoiceUcFirst('commons.video') }),
				description: this.transUcfirst('one.video_export.message', { clips_count: this.eventTags.length, duration: this.eventTagsDuration }),
				fields:      {
					title:            this.transUcfirst('commons.title'),
					intro:            `${this.transUcfirst('commons.intro')} (${this.trans('commons.optional')})`,
					outro:            `${this.transUcfirst('commons.outro')} (${this.trans('commons.optional')})`,
					introPlaceholder: this.transUcfirst('commons.type_choose', { type: this.transUcfirst('commons.intro') }),
					outroPlaceholder: this.transUcfirst('commons.type_choose', { type: this.transUcfirst('commons.outro') }),
					categoryParent:   this.transUcfirst('commons.category'),
					settings:         this.transUcfirst('commons.settings'),
				},
				introOutro: {
					title:        `${this.transUcfirst('commons.intro')} / ${this.transUcfirst('commons.outro')}`,
					message:      this.transUcfirst('one.video_export.intro_outro_info'),
					notAvailable: this.transUcfirst('one.video_export.intro_outro_na'),
				},
				copyTags: {
					title:   this.transUcfirst('commons.type_copy', { type: this.transChoiceUcFirst('commons.tag', this.eventTags.length) }),
					message: this.transUcfirst('one.video_export.copy_tags'),
				},
				enableAudioFade: {
					title:   this.transUcfirst('one.video_export.audio_fade_title'),
					message: this.transUcfirst('one.video_export.audio_fade_info'),
				},
			};
		},
		hasChanges() {
			return this.title !== this.generateTitle() || this.videoIntro !== null || this.videoOutro !== null;
		},
		introOutroNotAvailable() {
			return false;
		},
		audioFadeAvailable() {
			return this.eventTags.length > 1;
		},
		categoryParentOptions() {
			return this.sportCategoryParents ? this.sportCategoryParents.map((category) => {
				return {
					key:   `category_${category.id}`,
					id:    category.id,
					label: this.transUcfirst(`sports.${this.event?.sport?.name}.categories.parent.${category.name}`),
					value: category,
				};
			}) : [];
		},
		sportCategoryParents() {
			return this.$store.getters['event/sportCategoryParents'];
		},
		eventTagsDuration() {
			const durationSeconds = this.eventTags.reduce((totalDuration, tag) => {
				return totalDuration + tag.duration;
			}, 0);

			return formatTimestampAsMinutesSeconds(durationSeconds);
		},
	},
	watch: {
		sportCategoryParents() {
			this.setDefaultCategory();
		},
	},
	mounted() {
		this.title = this.generateTitle();
		this.loadEditorAssetVideos();
		this.setDefaultCategory();
	},
	methods: {
		...mapActions({
			cancelCreateVideo:     'videoCreate/cancel',
			loadEditorAssetVideos: 'videoCreate/loadEditorAssetVideos',
			createVideo:           'videoCreate/createVideo',
		}),
		close() {
			this.cancelCreateVideo();
		},
		setDefaultCategory() {
			this.categoryParent = this.sportCategoryParents ? this.sportCategoryParents.find(cat => cat.name === 'highlight') : null;
		},
		async save() {
			this.canSave = false;
			this.showExportModal = true;
			this.exportSuccessfull = false;
			this.exportFailed = false;
			this.isExporting = true;

			try {
				const startTime = new Date().getTime();
				const video = await this.createVideo({
					name:             this.title,
					eventTags:        this.eventTags,
					intro:            this.videoIntro,
					outro:            this.videoOutro,
					categoryParentId: this.categoryParent.id,
					copyTags:         this.copyTags,
					enableAudioFade:  this.enableAudioFade,
					eventTagGraphics: this.eventTagGraphics,
				});

				this.createdVideoLink = laroute.route('manager.event.edit', { event: video.id });

				const endTime = new Date().getTime();
				const duration = endTime - startTime;

				// Ensure the whole operation takes at least 4 seconds, to ensure a clean transition
				const remainingDelay = Math.max(0, 4000 - duration);

				// Delay if necessary
				if (remainingDelay > 0) {
					await new Promise(resolve => setTimeout(resolve, remainingDelay));
				}

				if (this.copyTags) {
					this.isCreatingTags = true;

					// Wait 2 seconds
					await new Promise(resolve => setTimeout(resolve, 2000));
				}

				this.exportSuccessfull = true;
			} catch (error) {
				console.log('Video Export Failed: ', JSON.stringify(error));
				this.exportFailed = true;
			} finally {
				this.canSave = true;
				this.isExporting = false;
			}
		},
		initFields() {
			this.title = this.generateTitle();
			this.videoIntro = null;
			this.videoOutro = null;
		},
		generateTitle() {
			if (this.eventTags.length > 1) {
				return `${this.event.name} - Custom Video`;
			}

			const tag = this.eventTags[0];

			if (tag.tag && this.event.sport && tag.name === tag.tag.name) {
				// Tag has no custom name (goal, warning, etc.). Include translated tag name in event title
				return `${this.event.name} - ${this.transTagLabel(tag.tag.name, this.event?.sport?.name, tag.tag.type)}`;
			}

			return tag.name;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import "@videotools/sass/_fonts";

.video-create-info {
	@include text-md-1;
}

.title {
	@include text-md-2;
	color: var(--color-soft-white);
	margin-bottom: 8px;
	display: flex;
	align-items: center;

	.has-tooltip {
		margin-left: auto;
	}
}

.is-mobile {
	.button-discard.disabled {
		display: none;
	}
}

.progress {
	margin: 30px 0;
	text-align: center;
}

.status {
	margin-bottom: 10px;
	display: flex;
	justify-content: center;

	&::v-deep a {
		color: var(--color-soft-white);
		text-decoration: underline;
	}
}

::v-deep {
	.mobile {
		.modal--footer {
			border-top: 1px solid var(--color-grey-800);
			padding: 16px;
		}

		.modal--header {
			border-bottom: initial;
		}
	}

}
</style>
